"use client";

import { useEffect } from "react";

const RELOAD_GUARD_TS_KEY = "chunk-reload-last-ts";
const RELOAD_COOLDOWN_MS = 12_000;

function shouldRecoverFromErrorMessage(message: string): boolean {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("chunkloaderror") ||
    m.includes("loading chunk") ||
    m.includes("failed to fetch dynamically imported module") ||
    (m.includes("refused to execute script") && m.includes("mime type")) ||
    m.includes("application error: a client-side exception")
  );
}

function isKnownExtensionNoise(message: string): boolean {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("a listener indicated an asynchronous response by returning true") ||
    m.includes("message channel closed before a response was received") ||
    m.includes("the message port closed before a response was received")
  );
}

export default function ChunkLoadRecovery() {
  useEffect(() => {
    const reloadOnce = () => {
      try {
        const now = Date.now();
        const lastTs = Number(sessionStorage.getItem(RELOAD_GUARD_TS_KEY) || 0);
        if (Number.isFinite(lastTs) && now - lastTs < RELOAD_COOLDOWN_MS) return;
        sessionStorage.setItem(RELOAD_GUARD_TS_KEY, String(now));
      } catch {
        // ignore
      }
      window.location.reload();
    };

    const onError = (event: Event) => {
      const errEvent = event as ErrorEvent;
      const msg =
        errEvent?.message ||
        (errEvent?.error instanceof Error ? errEvent.error.message : "");

      const target = (event as any)?.target as HTMLElement | null;
      const scriptSrc =
        target && target.tagName === "SCRIPT"
          ? String((target as HTMLScriptElement).src || "")
          : "";

      const hasChunkScriptFailure =
        scriptSrc.includes("/_next/static/chunks/") ||
        scriptSrc.includes("/_next/static/");

      if (shouldRecoverFromErrorMessage(msg) || hasChunkScriptFailure) {
        reloadOnce();
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const reasonName =
        reason && typeof reason === "object" ? String((reason as any).name || "") : "";
      const msg =
        typeof reason === "string"
          ? reason
          : reason instanceof Error
            ? reason.message
            : String(reason ?? "");
      if (isKnownExtensionNoise(msg)) {
        // خطای نویزی افزونه‌های مرورگر؛ از دید کاربر پنل خطا محسوب نمی‌شود.
        event.preventDefault();
        return;
      }
      if (shouldRecoverFromErrorMessage(msg) || reasonName.toLowerCase() === "chunkloaderror") {
        event.preventDefault();
        reloadOnce();
      }
    };

    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}
